import { createSelector } from '@reduxjs/toolkit';
import type { CompleteOrganizationViewModel, TaskViewModel } from '@trinnvis/dabih-api-client';
import { sortBy } from 'lodash';
import { joinWithAnd } from 'src/utilities/text';
import { t } from 'src/i18n';
import { currentEmployeeUuid } from './employees/current-employee-uuid';
import { employeesNotDeleted } from './employees/employees-not-deleted';
import { contactsNotDeleted } from './partners/contacts-not-deleted';
import { currentContactUuid } from './partners/current-contact-uuid';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';

interface CompleteOrganizationViewModelWithTasks extends CompleteOrganizationViewModel {
  tasks: TaskViewModel[];
}
interface EntityForAccess {
  uuid: string;
  accessType: string;
}

interface EntityScopeForaccess {
  accessType: string;
  scope: string;
  entities?: EntityForAccess[];
}

interface EntityTypeForAccess {
  name: keyof CompleteOrganizationViewModelWithTasks;
  scopes: EntityScopeForaccess[];
}

interface AccessProfile {
  name: string;
  displayName: string;
  description: string;
  userTypes: string[];
  accessType: string;
  pages?: number[];
  newDocMenu?: string[];
  entityTypes?: EntityTypeForAccess[];
}

export function accessProfiles(): AccessProfile[] {
  return [
    {
      name: 'NONE',
      displayName: t('access.none.displayName'),
      description: t('access.none.description'),
      userTypes: ['employees', 'contacts'],
      accessType: 'none',
    },
    {
      name: 'READONLY',
      displayName: t('access.readonly.displayName'),
      description: t('access.readonly.description'),
      userTypes: ['employees', 'contacts'],
      accessType: 'read',
    },
    {
      name: 'USER',
      displayName: t('access.fullAccess.displayName'),
      description: t('access.user.description'),
      userTypes: ['employees', 'contacts'],
      accessType: 'write',
    },
    {
      name: 'ADMIN',
      displayName: t('access.admin.displayName'),
      description: t('access.admin.description'),
      userTypes: ['employees'],
      accessType: 'write',
    },
    {
      name: 'ASSETS',
      displayName: t('access.assets.displayName'),
      description: t('access.assets.description'),
      userTypes: ['contacts'],
      accessType: 'specified',
      pages: [318434, 110],
      newDocMenu: ['assets'],
      entityTypes: [
        {
          name: 'employees',
          scopes: [
            {
              scope: 'all',
              accessType: 'minimal',
            },
          ],
        },
        {
          name: 'partners',
          scopes: [
            {
              scope: 'all',
              accessType: 'read',
            },
          ],
        },
        {
          name: 'assets',
          scopes: [
            {
              scope: 'all',
              accessType: 'write',
            },
          ],
        },
        {
          name: 'functions',
          scopes: [
            {
              scope: 'all',
              accessType: 'read',
            },
          ],
        },
        {
          name: 'tasks',
          scopes: [
            {
              scope: 'assetRelated',
              accessType: 'read',
            },
          ],
        },
      ],
    },
  ];
}
createSelector(
  currentEmployeeUuid,
  currentContactUuid,
  employeesNotDeleted,
  contactsNotDeleted,
  accessProfiles,
  function (employeeUuid, contactUuid, employees, contacts, profiles): AccessProfile {
    let profileName = '';
    if (employeeUuid) {
      profileName = employees.find((x) => x.uuid === employeeUuid)?.accessLevel ?? 'NONE';
    }
    if (contactUuid) {
      profileName = contacts.find((x) => x.uuid === contactUuid)?.accessLevel ?? 'NONE';
    }
    if (profileName) {
      return profiles.filter((p) => {
        return p.name === profileName;
      })[0];
    }
    return {
      name: 'default',
      description: '',
      userTypes: [],
      displayName: t('access.fullAccess.displayName'),
      accessType: 'write',
    };
  },
);

export function getClassificationText(
  classification: 'NONE' | 'CONFIDENTIAL' | 'PRIVATE',
  accessControl: string[],
  accessControlOptions: SelectDropdownOption[],
): string {
  if (classification !== 'NONE') {
    let s = accessControlOptions
      .filter(function (x) {
        return (accessControl ?? []).indexOf(x.value) >= 0;
      })
      .map(function (x) {
        return x.text;
      });
    s = sortBy(s, (e) => e.toLowerCase());
    return joinWithAnd(s);
  }
  return '';
}
